import { default as ReactSelect } from 'react-select';
import type { Props as ReactSelectProps } from 'react-select';
import classNames from 'classnames';

import { AsteriskIcon } from '../../icons';
import ErrorText from '../ErrorText';
import MultiOption from './components/MultiOption';

import styles from './_index.module.scss';

type SelectOption = {
  label: React.ReactNode;
  options?: Array<SelectOption>;
  value?: string | number;
};

export interface SelectProps {
  className?: string;
  'data-testid'?: string;
  error?: string;
  isDisabled?: boolean;
  isFullHeightMenuList?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  isRequired?: boolean;
  label?: string;
  onChange?: ReactSelectProps['onChange'];
  options?: Array<SelectOption>;
  placeholder?: string;
  value: ReactSelectProps['value'];
}

const Select = ({
  className,
  'data-testid': dataTestId,
  error,
  isDisabled,
  isFullHeightMenuList,
  isLoading,
  isMulti,
  isRequired,
  label,
  onChange,
  options,
  placeholder,
  value,
}: SelectProps) => {
  const otherClassNames = {
    [styles['is-error']]: error,
  };

  // SEARCH COMPATIBILITY FOR LABELS RENDERED AS REACT COMPONENTS, I.E. <Tag />

  const transformOptionLabel = (label: any) => {
    if (label?.props?.label) return label.props.label;
    if (typeof label === 'number') return label.toString();
    return label;
  };

  const filterOption: ReactSelectProps['filterOption'] = ({ label, value }, string) => {
    const optionLabel = transformOptionLabel(label);
    const isMatch = (option: any) =>
      typeof option === 'string' && option.toLowerCase().includes(string.toLowerCase());

    return isMatch(optionLabel) || isMatch(value);
  };

  return (
    <div data-testid={dataTestId}>
      {label && (
        <label className={styles['select-label']}>
          {label}
          {isRequired && <AsteriskIcon />}
        </label>
      )}
      <ReactSelect
        className={classNames(styles['select'], className, otherClassNames)}
        classNamePrefix="select"
        closeMenuOnSelect={!isMulti}
        // @ts-ignore: MultiOption type mismatch
        components={isMulti ? { Option: MultiOption } : {}}
        filterOption={filterOption}
        formatGroupLabel={(data) => <h4>{data?.label}</h4>}
        hideSelectedOptions={false}
        isClearable={isMulti}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isMulti={isMulti}
        onChange={onChange}
        options={options}
        placeholder={placeholder || 'Select option'}
        styles={{
          menu: (base) => ({
            ...base,
            minWidth: '100%',
            width: 'max-content',
          }),
          ...(isFullHeightMenuList && {
            menuList: (base) => ({
              ...base,
              maxHeight: 'none',
            }),
          }),
        }}
        value={value}
      />
      {error && <ErrorText className={styles['select-error']}>{error}</ErrorText>}
    </div>
  );
};

export default Select;
